// Ops route — Phase 6.A 운영 탭. Phase 5a analytics endpoints + 4 layout variants.

import { useEffect, useMemo, useState } from 'react';
import { AnalyticsControls, type Period, type Compare } from '../components/analytics/controls';
import {
  AnalyticsLayout,
  VariantPicker,
  useLayoutVariant,
} from '../components/analytics/AnalyticsLayouts';
import { PlatformTree, type Selection } from '../components/sidebar/PlatformTree';
import type { AnalyticsScope } from '../components/analytics/queries';
import { RecipeMissBanner } from '../components/analytics/RecipeMissBanner';
import { PageHeader } from '../components/chrome';
import { useViewport } from '../lib/useViewport';

const STORAGE_KEY = 'dvvb.analytics.v3.state';

interface SavedState {
  variant?: string;
  period?: Period;
  compare?: Compare;
  selection?: Selection;
}

function readSaved(): SavedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SavedState;
  } catch {
    return {};
  }
}

function writeSaved(s: SavedState) {
  try {
    const prev = readSaved();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...s }));
  } catch {
    /* noop */
  }
}

export function OpsRoute() {
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';
  const [variant, setVariant] = useLayoutVariant();
  const [period, setPeriod] = useState<Period>(
    () => readSaved().period ?? '7d',
  );
  const [compare, setCompare] = useState<Compare>(
    () => readSaved().compare ?? 'prev',
  );
  const [selection, setSelection] = useState<Selection>(
    () => readSaved().selection ?? { level: 'all' },
  );

  useEffect(() => {
    writeSaved({ period, compare, selection });
  }, [period, compare, selection]);

  const scope = useMemo<AnalyticsScope>(
    () => ({
      period,
      compare,
      storeId: selection.level === 'store' ? selection.storeId : null,
      platformId:
        selection.level === 'platform' ? selection.platformId : null,
    }),
    [period, compare, selection],
  );

  const today = new Date();
  const dateLabel = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* PageHeader 는 시안 design/v3/Chrome.jsx:89 의 padding "24px 24px 16px" 을 가지므로,
          main wrapper 의 좌우 24 padding 을 상쇄해 시안과 동일한 24 들여쓰기로 정렬. */}
      <div style={{ marginLeft: isMobile ? -14 : -24, marginRight: isMobile ? -14 : -24 }}>
        <PageHeader
          viewport={isMobile ? 'mobile' : 'desktop'}
          title="운영"
          date={`분석 · ${dateLabel}`}
        >
          <VariantPicker variant={variant} onChange={setVariant} />
        </PageHeader>
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            padding: isMobile ? '0 14px' : '0 24px',
            marginTop: -12,
          }}
        >
          플랫폼 · 상점별 성과{isMobile ? '' : ' — 좌측 트리에서 선택'}
        </div>
      </div>

      <div
        style={{
          // Phase 6.C-5 — 모바일 stack (PlatformTree 위 + 본문 아래)
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '260px minmax(0, 1fr)',
          gap: isMobile ? 12 : 24,
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            // 모바일에서는 sticky 해제 (위에 있어 자연스러움)
            position: isMobile ? 'static' : 'sticky',
            top: 72,
          }}
        >
          <PlatformTree
            selection={selection}
            onSelect={setSelection}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 2026-05-01 [4] — 레시피 미입력 메뉴 판매 alert (count>0 시만 노출). */}
          <RecipeMissBanner scope={scope} />
          <AnalyticsControls
            period={period}
            setPeriod={setPeriod}
            compare={compare}
            setCompare={setCompare}
          />
          <AnalyticsLayout
            variant={variant}
            scope={scope}
            onSelectPlatform={(platformId) =>
              setSelection({ level: 'platform', platformId })
            }
            onSelectStore={(storeId, platformId) =>
              setSelection({ level: 'store', storeId, platformId })
            }
            selectedPlatformId={
              selection.level === 'platform' ? selection.platformId : null
            }
            selectedStoreId={
              selection.level === 'store' ? selection.storeId : null
            }
          />
        </div>
      </div>
    </div>
  );
}
